/**
 * Ingredient Matching Section Component
 *
 * Collapsible section displaying all extracted ingredients with matching UI.
 * Shows summary counts for matched/review/new ingredients.
 */

import { useState } from 'react';
import type { ExtractedIngredient } from '@/types/recipeImport';
import type { Ingredient } from '@/types/food';
import { IngredientMatchingRow } from './IngredientMatchingRow';

interface IngredientMatchingSectionProps {
  ingredients: ExtractedIngredient[];
  availableIngredients: Ingredient[];
  onChange: (ingredients: ExtractedIngredient[]) => void;
}

export function IngredientMatchingSection({
  ingredients,
  availableIngredients,
  onChange,
}: IngredientMatchingSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  // Calculate summary counts
  const matchedCount = ingredients.filter(
    (ing) => !ing.is_new && ing.confidence >= 85
  ).length;
  const reviewCount = ingredients.filter(
    (ing) => !ing.is_new && ing.confidence >= 70 && ing.confidence < 85
  ).length;
  const newCount = ingredients.filter((ing) => ing.is_new || ing.confidence < 70).length;

  const handleIngredientChange = (index: number, updated: ExtractedIngredient) => {
    const newIngredients = [...ingredients];
    newIngredients[index] = updated;
    onChange(newIngredients);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <svg
            className="w-5 h-5 text-blue-600 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"
            />
          </svg>
          <div className="text-left">
            <h3 className="text-base font-semibold text-gray-900">
              Ingredient Matching
            </h3>
            <p className="text-sm text-gray-600 mt-0.5">
              Review and confirm ingredient matches
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Summary Counts */}
          <div className="flex items-center gap-2">
            {matchedCount > 0 && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {matchedCount} matched
              </span>
            )}
            {reviewCount > 0 && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                {reviewCount} review
              </span>
            )}
            {newCount > 0 && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {newCount} new
              </span>
            )}
          </div>

          {/* Expand/Collapse Icon */}
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${
              isExpanded ? 'rotate-180' : ''
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="px-6 pb-6">
          {ingredients.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No ingredients extracted
            </div>
          ) : (
            <div className="space-y-3">
              {ingredients.map((ingredient, index) => (
                <IngredientMatchingRow
                  key={`${ingredient.original_text}-${index}`}
                  ingredient={ingredient}
                  availableIngredients={availableIngredients}
                  onChange={(updated) => handleIngredientChange(index, updated)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
